import { Input, InputProps, InputRef } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import React, { FC, useEffect, useRef, useState } from 'react';

import { Button, PlaceholderAsLabel } from '~shared/ui';

interface InputKyProps extends InputProps {
  name?: string;
  required?: any;
  placeholder: string;
  size?: SizeType;
  className?: string;
  value?: any;
  displayButtons?: boolean;
  onChange?: (value: string | any) => any;
  forcedSHift?: boolean;
}

export const InputKy: FC<InputKyProps> = ({
  name,
  placeholder = '',
  size = 'large',
  required,
  value,
  displayButtons = true,
  onChange,
  forcedSHift,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value || '');
  const [isShiftPressed, setIsShiftPressed] = useState(false);

  useEffect(() => {
    setInternalValue(value || '');
  }, [value]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Shift') {
      setIsShiftPressed(true);
    }

    if (event.key === 'CapsLock') {
      const capsLockOn = event.getModifierState && event.getModifierState('CapsLock');
      setIsShiftPressed(capsLockOn);
    }

    props.onKeyDown?.(event);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Shift') {
      setIsShiftPressed(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChange && onChange(newValue);
  };

  // const handleClick = (button: string) => {
  //   const newChar = isShiftPressed ? button.toUpperCase() : button.toLowerCase();
  //   const updatedValue = internalValue + newChar;
  //   setInternalValue(updatedValue);
  //   onChange && onChange(updatedValue);
  // };

  const inputRef = useRef<InputRef>(null);

  const handleClick = (button: string) => {
    const newChar = isShiftPressed ? button.toUpperCase() : button.toLowerCase();

    if (inputRef.current?.input) {
      const inputElement = inputRef.current.input; // Access the native input element
      const cursorPositionStart = inputElement.selectionStart || 0;
      const cursorPositionEnd = inputElement.selectionEnd || 0;

      // Create the updated value by slicing and inserting the character
      const updatedValue =
        internalValue.slice(0, cursorPositionStart) +
        newChar +
        internalValue.slice(cursorPositionEnd);

      setInternalValue(updatedValue);
      onChange && onChange(updatedValue);

      // Adjust the cursor position to be after the inserted character
      setTimeout(() => {
        inputElement.setSelectionRange(
          cursorPositionStart + newChar.length,
          cursorPositionStart + newChar.length
        );
      }, 0);
    }
  };

  const buttons = ['Ө', 'Ү', 'Ң'];

  useEffect(() => {
    const handleWindowKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    window.addEventListener('keyup', handleWindowKeyUp);

    return () => window.removeEventListener('keyup', handleWindowKeyUp);
  }, []);

  return (
    <PlaceholderAsLabel placeholder={placeholder} value={value}>
      <Input
        ref={inputRef}
        placeholder={placeholder}
        required={required}
        value={internalValue}
        name={name}
        onChange={handleChange}
        size={size}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        suffix={
          displayButtons ? (
            <span className="flex gap-1">
              {buttons.map((button: string) => (
                <Button
                  size="small"
                  className="!p-0 bg-[#f0f0f0] hover:!bg-[#dedede] hover:!text-black !h-[22px] !w-[22px] !min-h-[22px] !rounded-full flex items-center justify-center border-none focus:outline-none"
                  key={button}
                  value={button}
                  onClick={() => handleClick(button)}
                >
                  <span className="text-[12px]">
                    {isShiftPressed || forcedSHift ? button.toUpperCase() : button.toLowerCase()}
                  </span>
                </Button>
              ))}
            </span>
          ) : null
        }
        {...props}
      />
    </PlaceholderAsLabel>
  );
};
